// Pokéball canvas animation — fired when an infographic card is hidden.
// Usage: animatePokeballs(originRect) where originRect is a DOMRect.

const BALL_COUNT = 32
const LIFETIME_MS = 1200
const GRAVITY = 0.38
const IMPULSE_Y = -2.2

// Symbol IDs matching _pokeball_sprites.html.erb, with rarity weights (must sum to 100)
const BALL_TYPES = [
  { id: 'pb-standard', weight: 50 },
  { id: 'pb-great',    weight: 30 },
  { id: 'pb-ultra',    weight: 19 },
  { id: 'pb-master',   weight:  1 },
]

function pickType() {
  let r = Math.random() * 100
  for (const { id, weight } of BALL_TYPES) {
    r -= weight
    if (r <= 0) return id
  }
  return BALL_TYPES[0].id
}

// ── Image preloading ─────────────────────────────────────────────────────────
// Each SVG symbol is rendered into an HTMLImageElement once at module load.
// canvas.drawImage is then used per frame — much cheaper than redrawing paths.
//
// We read the <symbol> element from the DOM, extract its viewBox + innerHTML,
// and wrap it in a self-contained SVG data URL (including the shared style).
// This avoids the browser restriction on cross-document <use href="#...">
// references inside data URLs rendered to canvas.

// Shared CSS classes from _pokeball_sprites.html.erb
const SPRITE_STYLE = [
  '.st0{fill:#FFFFFF;}', '.st1{fill:#F1F2F2;}', '.st2{fill:#00AEEF;}',
  '.st3{fill:#E6E7E8;}', '.st4{fill:#D1D3D4;}', '.st5{fill:#E65565;}',
  '.st6{fill:#DA2428;}', '.st7{fill:#1C75BC;}', '.st8{fill:#F9C713;}',
  '.st9{fill:#F7941E;}', '.st10{fill:#804968;}', '.st11{fill:#C0707D;}',
  '.st12{fill:#414042;}', '.st13{fill:#FFF0A6;}',
].join('')

function symbolToImage(symbolId, size = 128) {
  return new Promise((resolve) => {
    const symbolEl = document.getElementById(symbolId)
    if (!symbolEl) { resolve([symbolId, null]); return }

    const viewBox = symbolEl.getAttribute('viewBox') || '0 0 1 1'
    const inner = symbolEl.innerHTML

    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" width="${size}" height="${size}">` +
      `<defs><style>${SPRITE_STYLE}</style></defs>` +
      inner +
      `</svg>`

    const dataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)
    const img = new Image(size, size)
    img.onload = () => resolve([symbolId, img])
    img.onerror = () => resolve([symbolId, null])
    img.src = dataUrl
  })
}

let imagesReady = null

function preloadImages() {
  if (!imagesReady) {
    imagesReady = Promise.all(BALL_TYPES.map(({ id }) => symbolToImage(id))).then(
      (pairs) => new Map(pairs)
    )
  }
  return imagesReady
}

// Kick off preloading immediately so images are warm before the first hide
preloadImages()

// ── Particle factory ─────────────────────────────────────────────────────────

function createParticle(originRect, type) {
  const cx = originRect.x + originRect.width / 2
  const cy = originRect.y + originRect.height / 2

  const x = cx + (Math.random() - 0.5) * originRect.width * 1.1
  const y = cy + (Math.random() - 0.5) * originRect.height * 1.1

  const angle = Math.random() * Math.PI * 2 + (Math.random() - 0.5) * 1.0
  const speed = 2.5 + Math.random() * 4.5

  return {
    type,
    x,
    y,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed + IMPULSE_Y,
    radius: 8 + Math.random() * 10,
    rotation: Math.random() * Math.PI * 2,
    spin: (Math.random() - 0.5) * 0.48,
    delay: Math.random() * 180,
    startTime: null,
    done: false,
  }
}

// ── Main export ──────────────────────────────────────────────────────────────

export async function animatePokeballs(originRect, onComplete) {
  const images = await preloadImages()

  const canvas = document.createElement('canvas')
  canvas.width = window.innerWidth
  canvas.height = window.innerHeight
  canvas.style.cssText = 'position:fixed;top:0;left:0;pointer-events:none;z-index:9999;'
  document.body.appendChild(canvas)

  const ctx = canvas.getContext('2d')
  const type = pickType()
  const particles = Array.from({ length: BALL_COUNT }, () => createParticle(originRect, type))
  const img = images.get(type)

  function frame(timestamp) {
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    let allDone = true

    for (const p of particles) {
      if (p.startTime === null) {
        p.startTime = timestamp + p.delay
      }

      const elapsed = timestamp - p.startTime
      if (elapsed < 0) {
        allDone = false
        continue
      }

      const t = Math.min(elapsed / LIFETIME_MS, 1)
      if (t >= 1) {
        p.done = true
        continue
      }

      allDone = false

      // Physics update
      p.vy += GRAVITY
      p.x += p.vx
      p.y += p.vy
      p.rotation += p.spin

      // Fade
      const alpha = Math.max(0, 1 - t * t)
      ctx.globalAlpha = alpha

      // Draw SVG image rotated around its centre
      if (img) {
        const d = p.radius * 2
        ctx.save()
        ctx.translate(p.x, p.y)
        ctx.rotate(p.rotation)
        ctx.drawImage(img, -p.radius, -p.radius, d, d)
        ctx.restore()
      }

      ctx.globalAlpha = 1
    }

    if (allDone) {
      onComplete?.()
      canvas.remove()
      return
    }

    requestAnimationFrame(frame)
  }

  requestAnimationFrame(frame)
}
